// frontend/src/UserLogin.js
// UrbanQ v1.8.9.4.5.7 - Con verificacion 2FA
// Build 2026-02-19

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_ENDPOINTS } from './config/api';
import VerificationCode from './components/VerificationCode';
import './UserLogin.css';

export default function UserLogin({ onLogin }) {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Estados para 2FA
  const [step, setStep] = useState('login'); // 'login' | 'verify'
  const [pendingEmail, setPendingEmail] = useState('');
  const [pendingUserId, setPendingUserId] = useState(null);
  const [verificationError, setVerificationError] = useState('');

  // Mostrar mensaje de exito si viene de reset password
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      setTimeout(() => setSuccessMessage(''), 5000);
    }
  }, [location]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.login, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al iniciar sesion');
      }

      // Verificar si requiere 2FA
      if (data.requiresVerification) {
        setPendingEmail(data.email);
        setPendingUserId(data.userId);
        setStep('verify');
        return;
      }

      // Login directo (sin 2FA o 2FA deshabilitado)
      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        onLogin(data.user);
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Verificar codigo 2FA
  const handleVerify = async (code) => {
    setVerificationError('');

    try {
      const response = await fetch(API_ENDPOINTS.verifyCode, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: pendingUserId,
          email: pendingEmail,
          code: code
        })
      });

      const data = await response.json();

      if (data.success && data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        onLogin(data.user);
        navigate('/dashboard');
      } else {
        setVerificationError(data.message || 'Codigo invalido');
        if (data.codeExpired) {
          // Volver al login si el codigo expiro
          setTimeout(() => {
            setStep('login');
            setError('El codigo ha expirado. Por favor, inicia sesion nuevamente.');
          }, 2000);
        }
      }
    } catch (err) {
      setVerificationError('Error de conexion. Intenta de nuevo.');
    }
  };

  // Reenviar codigo
  const handleResend = async () => {
    try {
      await fetch(API_ENDPOINTS.resendCode, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: pendingUserId,
          email: pendingEmail
        })
      });
    } catch (err) {
      console.error('Error reenviando codigo:', err);
    }
  };

  // Volver al login
  const handleBack = () => {
    setStep('login');
    setVerificationError('');
    setPendingEmail('');
    setPendingUserId(null);
  };

  // Si estamos en paso de verificacion, mostrar componente 2FA
  if (step === 'verify') {
    return (
      <VerificationCode
        email={pendingEmail}
        userId={pendingUserId}
        onVerify={handleVerify}
        onResend={handleResend}
        onBack={handleBack}
        error={verificationError}
        loading={loading}
      />
    );
  }

  // Formulario de login normal
  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="card-left">
            <h2 className="login-title">Bienvenido</h2>
            <p className="login-subtitle">Inicia sesion en UrbanQ</p>

            {/* Mensaje de exito */}
            {successMessage && (
              <div className="success-message" style={{
                background: '#f0fff4',
                border: '2px solid #48bb78',
                color: '#22543d',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1rem',
                fontSize: '0.95rem'
              }}>
                {successMessage}
              </div>
            )}

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleLogin}>
              <div className="input-group">
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Usuario"
                  required
                  autoComplete="username"
                />
              </div>
              <div className="input-group">
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Contrasena"
                  required
                  autoComplete="current-password"
                />
              </div>

              {/* Enlace de recuperacion de contrasena */}
              <div style={{
                textAlign: 'right',
                marginBottom: '1rem',
                marginTop: '-0.5rem'
              }}>
                <span
                  onClick={() => navigate('/forgot-password')}
                  style={{
                    color: '#4A90E2',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    textDecoration: 'underline'
                  }}
                >
                  Olvidaste tu contrasena?
                </span>
              </div>

              <button
                type="submit"
                className="login-button"
                disabled={loading}
              >
                {loading ? 'Verificando...' : 'Iniciar Sesion'}
              </button>
            </form>

            <div style={{
              marginTop: '1.5rem',
              textAlign: 'center',
              fontSize: '0.9rem',
              color: '#666'
            }}>
              No tienes cuenta?{' '}
              <span
                onClick={() => navigate('/register')}
                style={{
                  color: '#4A90E2',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Registrate aqui
              </span>
            </div>
          </div>

          <div className="card-right" />
        </div>
      </div>
    </div>
  );
}
